import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Calendar,
  Clock,
  Edit,
  Plus,
  RefreshCw,
  Trash2,
  CheckCircle,
  XCircle,
  Info,
  CalendarDays,
} from "lucide-react";
import api from "../lib/api";

const LeavePolicy = () => {
  const { currentBusiness } = useAuth();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: "",
    name: "",
    accrual_rate: 1,
    carry_forward: false,
    max_carry_forward: 0,
    max_balance: 12,
    fixed_entitlement: false,
    description: "",
  });

  const fetchPolicies = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/leave-policies?business_id=${currentBusiness.id}`);
      setPolicies(res.data);
    } catch (error) {
      toast.error("Failed to load leave policies");
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const resetForm = () => {
    setFormData({
      leave_type: "",
      name: "",
      accrual_rate: 1,
      carry_forward: false,
      max_carry_forward: 0,
      max_balance: 12,
      fixed_entitlement: false,
      description: "",
    });
    setEditingPolicy(null);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      leave_type: policy.leave_type,
      name: policy.name,
      accrual_rate: policy.accrual_rate,
      carry_forward: policy.carry_forward,
      max_carry_forward: policy.max_carry_forward,
      max_balance: policy.max_balance,
      fixed_entitlement: policy.fixed_entitlement || false,
      description: policy.description || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPolicy) {
        await api.put(`/leave-policies/${editingPolicy.id}`, formData);
        toast.success("Policy updated successfully");
      } else {
        await api.post("/leave-policies", {
          ...formData,
          business_id: currentBusiness.id,
        });
        toast.success("Policy created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchPolicies();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save policy");
    }
  };

  const handleDelete = async (policy) => {
    if (!window.confirm(`Delete "${policy.name}" policy? This cannot be undone.`)) return;
    try {
      await api.delete(`/leave-policies/${policy.id}`);
      toast.success("Policy deleted");
      fetchPolicies();
    } catch (error) {
      toast.error("Failed to delete policy");
    }
  };

  const toggleActive = async (policy) => {
    try {
      await api.put(`/leave-policies/${policy.id}`, {
        ...policy,
        is_active: !policy.is_active,
      });
      toast.success(`Policy ${policy.is_active ? "disabled" : "enabled"}`);
      fetchPolicies();
    } catch (error) {
      toast.error("Failed to update policy");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <CalendarDays className="h-7 w-7 text-primary" />
            Leave Policy
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Configure leave types, accrual rates, and carry-forward rules
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchPolicies} className="rounded-full">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            className="btn-primary"
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            data-testid="add-policy-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Policy
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-violet-600 mt-0.5" />
            <div className="text-sm text-violet-700">
              <p className="font-medium">How Leave Accrual Works</p>
              <p className="text-violet-600 mt-1">
                Leave balance is calculated from each employee's <strong>join date</strong> using the monthly accrual rate.
                For example, if an employee joined 6 months ago with a 1 day/month accrual rate, they would have 6 days available.
                Carry-forward allows unused leaves from the previous year to be added to the current balance (up to the max limit).
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Policies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg">Leave Types</CardTitle>
          <CardDescription>
            {policies.length} leave {policies.length === 1 ? "type" : "types"} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Accrual Rate</TableHead>
                  <TableHead>Max Balance</TableHead>
                  <TableHead>Carry Forward</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {policies.map((policy) => (
                  <TableRow key={policy.id} className={!policy.is_active ? "opacity-50" : ""}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-800">{policy.name}</p>
                        <p className="text-xs text-slate-500">{policy.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {policy.fixed_entitlement ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          Fixed
                        </Badge>
                      ) : (
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-slate-400" />
                          <span className="font-medium">{policy.accrual_rate}</span>
                          <span className="text-slate-500 text-sm">day{policy.accrual_rate !== 1 ? "s" : ""}/month</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium">{policy.max_balance}</span>
                      <span className="text-slate-500 text-sm ml-1">days</span>
                    </TableCell>
                    <TableCell>
                      {policy.carry_forward ? (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-green-700 text-sm">
                            Yes <span className="text-slate-400">(max {policy.max_carry_forward})</span>
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1">
                          <XCircle className="h-4 w-4 text-slate-400" />
                          <span className="text-slate-500 text-sm">No</span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={policy.is_active 
                          ? "bg-green-50 text-green-700 border-green-200" 
                          : "bg-slate-50 text-slate-500 border-slate-200"}
                      >
                        {policy.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(policy)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          {policy.is_active ? "Disable" : "Enable"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(policy)}
                          className="text-primary hover:text-primary/80"
                          data-testid={`edit-policy-${policy.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(policy)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-policy-${policy.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {policies.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      No leave policies configured. Click "Add Policy" to create one.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Policy Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {policies.filter(p => p.is_active).map((policy) => (
          <Card key={policy.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-slate-800">{policy.name}</h3>
                  <p className="text-2xl font-bold text-primary mt-1">
                    {policy.fixed_entitlement ? policy.max_balance : `${policy.accrual_rate * 12}`}
                    <span className="text-sm font-normal text-slate-500 ml-1">days/year</span>
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-primary/20" />
              </div>
              <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500 space-y-1">
                <p>
                  {policy.fixed_entitlement 
                    ? "Fixed entitlement" 
                    : `Accrues ${policy.accrual_rate} day${policy.accrual_rate !== 1 ? "s" : ""}/month`}
                </p>
                <p>
                  {policy.carry_forward 
                    ? `Carry forward up to ${policy.max_carry_forward} days` 
                    : "No carry forward"}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add/Edit Policy Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              {editingPolicy ? "Edit Leave Policy" : "Add Leave Policy"}
            </DialogTitle>
            <DialogDescription>
              Configure accrual rules for this leave type
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Leave Type */}
            {!editingPolicy && (
              <div className="space-y-2">
                <Label>Leave Type ID</Label>
                <Select
                  value={formData.leave_type}
                  onValueChange={(v) => setFormData({ ...formData, leave_type: v })}
                >
                  <SelectTrigger className="input-field" data-testid="leave-type-select">
                    <SelectValue placeholder="Select leave type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual Leave</SelectItem>
                    <SelectItem value="sick">Sick Leave</SelectItem>
                    <SelectItem value="annual">Annual Leave</SelectItem>
                    <SelectItem value="personal">Personal Leave</SelectItem>
                    <SelectItem value="maternity">Maternity Leave</SelectItem>
                    <SelectItem value="paternity">Paternity Leave</SelectItem>
                    <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                    <SelectItem value="comp_off">Compensatory Off</SelectItem>
                    <SelectItem value="bereavement">Bereavement Leave</SelectItem>
                    <SelectItem value="study">Study Leave</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Display Name */}
            <div className="space-y-2">
              <Label>Display Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Casual Leave"
                className="input-field"
                required
                data-testid="policy-name-input"
              />
            </div>

            {/* Fixed Entitlement Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Fixed Entitlement</Label>
                <p className="text-xs text-slate-500">No monthly accrual (e.g., Maternity)</p>
              </div>
              <Switch
                checked={formData.fixed_entitlement}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  fixed_entitlement: checked,
                  accrual_rate: checked ? 0 : 1
                })}
                data-testid="fixed-entitlement-switch"
              />
            </div>

            {/* Accrual Rate (only if not fixed) */}
            {!formData.fixed_entitlement && (
              <div className="space-y-2">
                <Label>Accrual Rate (days per month)</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="10"
                  value={formData.accrual_rate}
                  onChange={(e) => setFormData({ ...formData, accrual_rate: parseFloat(e.target.value) || 0 })}
                  className="input-field"
                  data-testid="accrual-rate-input"
                />
                <p className="text-xs text-slate-500">
                  = {(formData.accrual_rate * 12).toFixed(1)} days per year
                </p>
              </div>
            )}

            {/* Max Balance */}
            <div className="space-y-2">
              <Label>Maximum Balance (days)</Label>
              <Input
                type="number"
                min="1"
                max="365"
                value={formData.max_balance}
                onChange={(e) => setFormData({ ...formData, max_balance: parseInt(e.target.value) || 0 })}
                className="input-field"
                data-testid="max-balance-input"
              />
              <p className="text-xs text-slate-500">
                Caps the maximum leave balance an employee can have
              </p>
            </div>

            {/* Carry Forward Toggle */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-sm font-medium">Allow Carry Forward</Label>
                <p className="text-xs text-slate-500">Unused leaves carry to next year</p>
              </div>
              <Switch
                checked={formData.carry_forward}
                onCheckedChange={(checked) => setFormData({ 
                  ...formData, 
                  carry_forward: checked,
                  max_carry_forward: checked ? 6 : 0
                })}
                data-testid="carry-forward-switch"
              />
            </div>

            {/* Max Carry Forward (only if carry forward enabled) */}
            {formData.carry_forward && (
              <div className="space-y-2">
                <Label>Max Carry Forward (days)</Label>
                <Input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_carry_forward}
                  onChange={(e) => setFormData({ ...formData, max_carry_forward: parseInt(e.target.value) || 0 })}
                  className="input-field"
                  data-testid="max-carry-forward-input"
                />
              </div>
            )}

            {/* Description */}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this leave type"
                className="input-field min-h-[60px]"
                data-testid="policy-description-input"
              />
            </div>

            <Button type="submit" className="w-full btn-primary" data-testid="save-policy-btn">
              {editingPolicy ? "Update Policy" : "Create Policy"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavePolicy;
